<template>
  <v-row justify="center" align="center">
    <v-col cols="12" sm="8" md="6">
      <!-- Transparent so the layout's page colour shows through: on the light
           theme's grey page a filled card would be a white band. -->
      <v-card class="pt-16 d-flex justify-center" flat color="transparent">
        <p style="font-family: Orbitron; font-size: 30pt">cynque</p>
      </v-card>
      <LoginCard
        :is-loading="isLoading"
        :auth-methods="authMethods"
        :are-passkeys-unavailable-here="arePasskeysUnavailableHere"
        :passkey-host="passkeyHost"
        @log-in="login"
        @passkey-sign-in="signInWithPasskeyButton"
        @password-form-visible="onPasswordFormVisible"
      />
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from '@simplewebauthn/browser'
import { authenticateUser, AuthenticateUserArgs } from '@/services/users'
import { completeLogin } from '~/cynqueModules/completeLogin'
import { addNotification } from '~/cynqueModules/addNotification'
import {
  PasskeyError,
  PasskeysUnavailableHereError,
  ServiceError,
} from '~/services/errors'
import {
  getAuthenticationMethods,
  getPasskeysUnavailableHere,
  signInWithPasskey,
} from '~/services/passkeys'
import { describeWebAuthnError } from '~/cynqueModules/webAuthnErrors'
import { requestPasskeyNudge } from '~/cynqueModules/passkeyNudge'
import type { TAuthMethod } from '~/types/passkey/models'
import { useLoggedInUserStore } from '~/stores/loggedInUser'

// Nuxt 4 ignores the Options-API `layout`/`transition` component options —
// page layout + transition are declared via the definePageMeta macro instead.
definePageMeta({
  layout: 'login',
  pageTransition: { name: 'fade', mode: 'out-in' },
})

export default defineComponent({
  data() {
    return {
      mn: '[pages/Login/index.vue]',
      isLoading: false,
      authMethods: null as TAuthMethod[] | null,
      arePasskeysUnavailableHere: false,
      // Where passkeys work instead, when this address can't use them.
      passkeyHost: undefined as string | undefined,
    }
  },
  computed: {
    loggedInUser() {
      return useLoggedInUserStore().getLoggedInUser
    },
  },
  watch: {
    loggedInUser(newVal) {
      if (!newVal && this.isLoading) {
        this.isLoading = false
      }
    },
  },
  async mounted() {
    await this.loadAuthMethods()
    if (this.authMethods?.includes('passkey')) {
      await this.checkPasskeysAvailableHere()
    }
  },
  beforeUnmount() {
    // A pending autofill ceremony would otherwise outlive the page.
    WebAuthnAbortService.cancelCeremony()
  },
  methods: {
    async loadAuthMethods() {
      const fn = `${this.mn} loadAuthMethods |`
      try {
        this.authMethods = await getAuthenticationMethods()
      } catch (e) {
        // Never strand a user on a card with nothing to press: offer both and
        // let the api refuse whichever this deployment has switched off.
        console.warn(`${fn} Could not load sign-in methods; offering both.`, e)
        this.authMethods = ['passkey', 'password']
      }
    },
    /**
     * On a host passkeys can't work on (a review app, local dev on the staging
     * api), drop the passkey button before anyone presses it, so the card opens
     * on the password form. A failed check leaves the button up: pressing it
     * makes the same check and says why it can't work.
     */
    markPasskeysUnavailableHere(e: PasskeysUnavailableHereError) {
      this.arePasskeysUnavailableHere = true
      this.passkeyHost = e.passkeyHost
    },
    async checkPasskeysAvailableHere() {
      const fn = `${this.mn} checkPasskeysAvailableHere |`
      try {
        const unavailable = await getPasskeysUnavailableHere()
        if (unavailable) {
          this.markPasskeysUnavailableHere(unavailable)
        }
      } catch (e) {
        console.debug(`${fn} Could not check; leaving passkeys on offer.`, e)
      }
    },
    /**
     * The autofill ceremony needs the email field, and only the password form
     * has one: the passkey screen is just its button. So the ceremony runs
     * while that form is on screen (the card emits after rendering it, so the
     * field is in the DOM), and stops when the user goes back to passkeys.
     */
    onPasswordFormVisible(isVisible: boolean) {
      if (!isVisible) {
        WebAuthnAbortService.cancelCeremony()
        return
      }
      if (this.authMethods?.includes('passkey')) {
        this.startAutofillSignIn()
      }
    },
    /**
     * Conditional UI: the browser offers the user's passkey in the email
     * field's autofill. This stays pending until they pick one, and is aborted
     * (silently) when they go back to the passkey screen.
     *
     * Its failures are not reported unless the api refused a passkey the user
     * actually picked: everything else either happened without the user doing
     * anything (opening the password form) or is the abort above.
     */
    async startAutofillSignIn() {
      const fn = `${this.mn} startAutofillSignIn |`
      if (this.arePasskeysUnavailableHere) {
        return
      }
      if (!(await browserSupportsWebAuthnAutofill())) {
        console.debug(`${fn} Browser has no passkey autofill.`)
        return
      }
      let authToken: string
      try {
        authToken = await signInWithPasskey({ useBrowserAutofill: true })
      } catch (e) {
        if (e instanceof PasskeysUnavailableHereError) {
          this.markPasskeysUnavailableHere(e)
        } else if (e instanceof PasskeyError) {
          this.notifyPasskeyFailure(e)
        } else {
          console.debug(`${fn} Autofill sign-in ended:`, e)
        }
        return
      }
      await this.finishSignIn(authToken)
    },
    async signInWithPasskeyButton() {
      const fn = `${this.mn} signInWithPasskeyButton |`
      this.isLoading = true
      let authToken: string
      try {
        authToken = await signInWithPasskey()
      } catch (e) {
        this.isLoading = false
        console.debug(`${fn} Passkey sign-in failed:`, e)
        if (e instanceof PasskeysUnavailableHereError) {
          this.markPasskeysUnavailableHere(e)
        }
        this.notifyPasskeyFailure(e)
        return
      }
      await this.finishSignIn(authToken)
    },
    notifyPasskeyFailure(e: unknown) {
      const message = describeWebAuthnError(e, 'get')
      if (message) {
        addNotification({
          type: 'warning',
          message,
          statusCode:
            e instanceof ServiceError && e.statusCode
              ? e.statusCode
              : undefined,
        })
      }
    },
    async finishSignIn(authToken: string) {
      this.isLoading = true // purposely not clearing on success. Visually distracting.
      try {
        await completeLogin(authToken)
      } catch (e) {
        this.notifyLoginFailure(e)
      }
    },
    notifyLoginFailure(e: unknown) {
      this.isLoading = false
      console.dir(e)
      const statusCode =
        e instanceof ServiceError && e.statusCode ? e.statusCode : undefined
      const message =
        e instanceof Error && e.message
          ? e.message
          : 'There was a problem logging you in. Please try again.' +
            ' If the problem persists, please call your sys admin.'
      addNotification({
        type: 'warning',
        message,
        statusCode,
      })
    },
    async login(loginBody: AuthenticateUserArgs) {
      const fn = `${this.mn} login |`
      console.debug(`${fn} Calling authenticateUser`)
      this.isLoading = true // purposely not clearing on success. Visually distracting.
      try {
        const authToken = await authenticateUser(loginBody)
        console.debug(`${fn} Authentication succeeded (for 'real' user)`)
        if (!authToken) {
          // Only on the server, which never runs this page (ssr: false).
          throw new Error('The sign-in did not return a session.')
        }
        // A password sign-in where a passkey would have worked: offer to make
        // one once the user lands (components/passkeys/PasskeyNudgeDialog.vue).
        if (
          this.authMethods?.includes('passkey') &&
          !this.arePasskeysUnavailableHere
        ) {
          requestPasskeyNudge()
        }
        await completeLogin(authToken)
      } catch (e) {
        this.notifyLoginFailure(e)
      }
    },
  },
})
</script>
